import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
    Search,
    SlidersHorizontal,
    BriefcaseBusiness,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    X,
    Building2,
} from "lucide-react";

import JobCard from "../component/JobCard";
import JobDetailsModal from "../component/JobDetailModel";
import "./JobPage.css";

const JOBS_API = "/api/jobs";
const PARTNER_API = "/api/partners";

const ITEMS_PER_PAGE = 6;

/* =========================================================
   HELPERS
========================================================= */

const getSkills = (skills) => {
    if (!skills) return [];

    if (Array.isArray(skills)) {
        return skills
            .flatMap((item) => String(item).split(","))
            .map((item) => item.trim())
            .filter(Boolean);
    }

    return String(skills)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
};

const getPartnerName = (partner) => {
    if (!partner) return "";

    return (
        partner.companyName ||
        partner.name ||
        partner.company ||
        partner.organizationName ||
        ""
    );
};

const getCompanyLocation = (partner, job) => {
    if (partner?.city && partner?.state) {
        return `${partner.city}, ${partner.state}`;
    }

    if (partner?.city) {
        return partner.city;
    }

    if (partner?.state) {
        return partner.state;
    }

    return job?.location || "India";
};

const formatDate = (date) => {
    if (!date) return "Not specified";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
        return "Not specified";
    }

    return parsedDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatSalary = (min, max) => {
    const minimum = Number(min);
    const maximum = Number(max);

    if (
        !Number.isFinite(minimum) &&
        !Number.isFinite(maximum)
    ) {
        return "Salary not disclosed";
    }

    const formatAmount = (amount) => {
        if (!Number.isFinite(amount) || amount <= 0) {
            return "";
        }

        if (amount >= 100000) {
            return `₹${(amount / 100000)
                .toFixed(1)
                .replace(".0", "")} LPA`;
        }

        return `₹${new Intl.NumberFormat("en-IN").format(
            amount
        )}`;
    };

    if (minimum > 0 && maximum > 0) {
        return `${formatAmount(minimum)} – ${formatAmount(
            maximum
        )}`;
    }

    if (minimum > 0) {
        return `From ${formatAmount(minimum)}`;
    }

    if (maximum > 0) {
        return `Up to ${formatAmount(maximum)}`;
    }

    return "Salary not disclosed";
};

const normalizeJob = (job, partner = null) => {
    const partnerName = getPartnerName(partner);

    return {
        ...job,
        partner,

        parsedSkills: getSkills(job?.skillsRequired),

        companyDisplayName:
            partnerName ||
            job?.companyName ||
            "CertED Partner Company",

        companyLocation: getCompanyLocation(
            partner,
            job
        ),

        salaryDisplay: formatSalary(
            job?.salaryMin,
            job?.salaryMax
        ),

        deadlineDisplay: formatDate(
            job?.applicationDeadline
        ),
    };
};

/* =========================================================
   COMPONENT
========================================================= */

const Jobs = () => {
    const navigate = useNavigate();

    const [jobs, setJobs] = useState([]);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [error, setError] = useState("");

    const [search, setSearch] = useState("");

    /*
     * IMPORTANT:
     * This page only displays APPROVED jobs.
     */
    const [statusFilter] = useState("APPROVED");

    const [employeeTypeFilter, setEmployeeTypeFilter] =
        useState("ALL");

    const [experienceFilter, setExperienceFilter] =
        useState("ALL");

    const [sortBy, setSortBy] = useState("newest");

    const [currentPage, setCurrentPage] = useState(1);

    const [selectedJob, setSelectedJob] = useState(null);

    const [showFilters, setShowFilters] = useState(false);

    /* =====================================================
       FETCH PARTNER
    ===================================================== */

    const fetchPartner = async (partnerId) => {
        if (
            partnerId === null ||
            partnerId === undefined ||
            String(partnerId).trim() === ""
        ) {
            return null;
        }

        try {
            const response = await fetch(
                `${PARTNER_API}/${partnerId}`
            );

            if (!response.ok) {
                return null;
            }

            return await response.json();
        } catch (err) {
            console.warn(
                `Partner ${partnerId} could not be loaded.`,
                err
            );

            return null;
        }
    };

    /* =====================================================
       LOAD JOBS
       ONLY APPROVED JOBS
    ===================================================== */

    const loadJobs = async (isRefresh = false) => {
        try {
            setError("");

            if (isRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            const response = await fetch(JOBS_API);

            if (!response.ok) {
                throw new Error(
                    `Jobs API returned ${response.status}`
                );
            }

            const data = await response.json();

            const jobsArray = Array.isArray(data)
                ? data
                : data?.content ||
                  data?.jobs ||
                  data?.data ||
                  [];

            /* ---------------------------------------------
               IMPORTANT:
               ONLY APPROVED JOBS
            --------------------------------------------- */

            const approvedJobs = jobsArray.filter(
                (job) =>
                    String(job?.status || "")
                        .trim()
                        .toUpperCase() === "APPROVED"
            );

            console.log(
                "All jobs from API:",
                jobsArray
            );

            console.log(
                "Approved jobs:",
                approvedJobs
            );

            /* ---------------------------------------------
               Partner cache
            --------------------------------------------- */

            const partnerCache = new Map();

            const uniquePartnerIds = [
                ...new Set(
                    approvedJobs
                        .map(
                            (job) => job?.postedBy
                        )
                        .filter(
                            (id) =>
                                id !== null &&
                                id !== undefined &&
                                String(id).trim() !== ""
                        )
                        .map((id) => String(id))
                ),
            ];

            await Promise.all(
                uniquePartnerIds.map(
                    async (partnerId) => {
                        const partner =
                            await fetchPartner(
                                partnerId
                            );

                        partnerCache.set(
                            partnerId,
                            partner
                        );
                    }
                )
            );

            /* ---------------------------------------------
               Normalize approved jobs
            --------------------------------------------- */

            const normalizedJobs =
                approvedJobs.map((job) => {
                    const partner =
                        partnerCache.get(
                            String(job?.postedBy)
                        ) || null;

                    return normalizeJob(
                        job,
                        partner
                    );
                });

            setJobs(normalizedJobs);

            setCurrentPage(1);
        } catch (err) {
            console.error(
                "Jobs loading error:",
                err
            );

            setError(
                "Unable to load jobs right now. Please check your backend API."
            );

            setJobs([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    /* =====================================================
       INITIAL LOAD
    ===================================================== */

    useEffect(() => {
        loadJobs();
    }, []);

    /* =====================================================
       RESET PAGE WHEN FILTER CHANGES
    ===================================================== */

    useEffect(() => {
        setCurrentPage(1);
    }, [
        search,
        employeeTypeFilter,
        experienceFilter,
        sortBy,
    ]);

    /* =====================================================
       EMPLOYMENT TYPES
    ===================================================== */

    const employeeTypes = useMemo(() => {
        const values = jobs
            .map(
                (job) => job?.employeeType
            )
            .filter(Boolean)
            .map((item) =>
                String(item).trim()
            );

        return [
            "ALL",
            ...new Set(values),
        ];
    }, [jobs]);

    /* =====================================================
       FILTER + SEARCH + SORT
    ===================================================== */

    const filteredJobs = useMemo(() => {
        const searchValue =
            search.trim().toLowerCase();

        let result = jobs.filter((job) => {
            const searchableText = [
                job?.jobTitle,
                job?.companyName,
                job?.companyDisplayName,
                job?.location,
                job?.companyLocation,
                job?.employeeType,
                job?.experienceRequired,
                job?.jobDescription,
                job?.jobRole,
                ...(job?.parsedSkills || []),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

            /* ---------------------------------------------
               Search
            --------------------------------------------- */

            const matchesSearch =
                !searchValue ||
                searchableText.includes(
                    searchValue
                );

            /* ---------------------------------------------
               STATUS
               Extra safety check:
               Only APPROVED can ever appear.
            --------------------------------------------- */

            const jobStatus = String(
                job?.status || ""
            )
                .trim()
                .toUpperCase();

            const matchesStatus =
                jobStatus === "APPROVED";

            /* ---------------------------------------------
               Employment Type
            --------------------------------------------- */

            const matchesEmployeeType =
                employeeTypeFilter === "ALL" ||
                String(
                    job?.employeeType || ""
                )
                    .toLowerCase()
                    .trim() ===
                    employeeTypeFilter
                        .toLowerCase()
                        .trim();

            /* ---------------------------------------------
               Experience
            --------------------------------------------- */

            let matchesExperience = true;

            const experience = String(
                job?.experienceRequired || ""
            ).toLowerCase();

            if (
                experienceFilter ===
                "FRESHER"
            ) {
                matchesExperience =
                    experience.includes(
                        "fresher"
                    ) ||
                    experience.includes("0");
            }

            if (
                experienceFilter ===
                "0-2"
            ) {
                matchesExperience =
                    experience.includes("0-2") ||
                    experience.includes("0 - 2") ||
                    experience.includes("1") ||
                    experience.includes("2");
            }

            if (
                experienceFilter ===
                "3-5"
            ) {
                matchesExperience =
                    experience.includes("3") ||
                    experience.includes("4") ||
                    experience.includes("5");
            }

            if (
                experienceFilter ===
                "5+"
            ) {
                matchesExperience =
                    experience.includes("5+") ||
                    experience.includes("6") ||
                    experience.includes("7") ||
                    experience.includes("8") ||
                    experience.includes("9") ||
                    experience.includes("10");
            }

            return (
                matchesSearch &&
                matchesStatus &&
                matchesEmployeeType &&
                matchesExperience
            );
        });

        /* =================================================
           SORT
        ================================================= */

        result = [...result].sort(
            (a, b) => {
                if (
                    sortBy ===
                    "newest"
                ) {
                    return (
                        new Date(
                            b?.createdAt || 0
                        ).getTime() -
                        new Date(
                            a?.createdAt || 0
                        ).getTime()
                    );
                }

                if (
                    sortBy ===
                    "oldest"
                ) {
                    return (
                        new Date(
                            a?.createdAt || 0
                        ).getTime() -
                        new Date(
                            b?.createdAt || 0
                        ).getTime()
                    );
                }

                if (
                    sortBy ===
                    "salary-high"
                ) {
                    return (
                        Number(
                            b?.salaryMax || 0
                        ) -
                        Number(
                            a?.salaryMax || 0
                        )
                    );
                }

                if (
                    sortBy ===
                    "salary-low"
                ) {
                    return (
                        Number(
                            a?.salaryMin || 0
                        ) -
                        Number(
                            b?.salaryMin || 0
                        )
                    );
                }

                return 0;
            }
        );

        return result;
    }, [
        jobs,
        search,
        employeeTypeFilter,
        experienceFilter,
        sortBy,
    ]);

    /* =====================================================
       PAGINATION
    ===================================================== */

    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredJobs.length /
                ITEMS_PER_PAGE
        )
    );

    const paginatedJobs = useMemo(() => {
        const start =
            (currentPage - 1) *
            ITEMS_PER_PAGE;

        return filteredJobs.slice(
            start,
            start + ITEMS_PER_PAGE
        );
    }, [
        filteredJobs,
        currentPage,
    ]);

    /* =====================================================
       CLEAR FILTERS
    ===================================================== */

    const clearFilters = () => {
        setSearch("");
        setEmployeeTypeFilter("ALL");
        setExperienceFilter("ALL");
        setSortBy("newest");
        setCurrentPage(1);
    };

    const activeFilterCount = [
        employeeTypeFilter !== "ALL",
        experienceFilter !== "ALL",
    ].filter(Boolean).length;

    /* =====================================================
       STATS
    ===================================================== */

    const companyCount = useMemo(() => {
        return new Set(
            jobs
                .map(
                    (job) =>
                        job?.companyDisplayName
                )
                .filter(Boolean)
        ).size;
    }, [jobs]);

    const vacancyCount = useMemo(() => {
        return jobs.reduce(
            (total, job) =>
                total +
                Number(
                    job?.vacancy || 0
                ),
            0
        );
    }, [jobs]);

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <div className="jobs-page">

            {/* =================================================
               BACKGROUND
            ================================================= */}

            <div className="jobs-bg-grid"></div>

            <div className="jobs-glow jobs-glow-one"></div>

            <div className="jobs-glow jobs-glow-two"></div>

            {/* =================================================
               HERO
            ================================================= */}

            <section className="jobs-hero">

                <div className="jobs-hero-content">

                    <h1>
                        Find Your Next
                        <span>
                            {" "}
                            Opportunity
                        </span>
                    </h1>

                    <p>
                        Explore verified opportunities
                        from our partner companies and
                        take the next step in your career.
                    </p>

                    <div className="jobs-hero-stats">

                        <div className="jobs-stat">
                            <strong>
                                {jobs.length}
                            </strong>

                            <span>
                                Open Jobs
                            </span>
                        </div>

                        <div className="jobs-stat-divider"></div>

                        <div className="jobs-stat">
                            <strong>
                                {companyCount}
                            </strong>

                            <span>
                                Companies
                            </span>
                        </div>

                        <div className="jobs-stat-divider"></div>

                        <div className="jobs-stat">
                            <strong>
                                {vacancyCount}
                            </strong>

                            <span>
                                Vacancies
                            </span>
                        </div>

                    </div>

                </div>

                <div className="jobs-hero-decoration">

                    <div className="hero-orbit orbit-one"></div>

                    <div className="hero-orbit orbit-two"></div>

                    <div className="hero-orbit orbit-three"></div>

                    <div className="hero-building-card">

                        <Building2 size={38} />

                        <div>
                            <strong>
                                Career Hub
                            </strong>

                            <span>
                                Build your future
                            </span>
                        </div>

                    </div>

                </div>

            </section>

            {/* =================================================
               SEARCH TOOLBAR
            ================================================= */}

            <section className="jobs-toolbar-section">

                <div className="jobs-toolbar">

                    <div className="jobs-search-box">

                        <Search size={19} />

                        <input
                            type="text"
                            placeholder="Search jobs, skills, companies or locations..."
                            value={search}
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                        />

                        {search && (
                            <button
                                type="button"
                                className="jobs-search-clear"
                                onClick={() =>
                                    setSearch("")
                                }
                                aria-label="Clear search"
                            >
                                <X size={16} />
                            </button>
                        )}

                    </div>

                    <button
                        type="button"
                        className={`jobs-filter-toggle ${
                            showFilters
                                ? "active"
                                : ""
                        }`}
                        onClick={() =>
                            setShowFilters(
                                !showFilters
                            )
                        }
                    >

                        <SlidersHorizontal
                            size={18}
                        />

                        <span>
                            Filters
                        </span>

                        {activeFilterCount >
                            0 && (
                            <span className="filter-count">
                                {activeFilterCount}
                            </span>
                        )}

                    </button>

                    <button
                        type="button"
                        className="jobs-refresh-btn"
                        onClick={() =>
                            loadJobs(true)
                        }
                        disabled={
                            refreshing
                        }
                        title="Refresh jobs"
                    >

                        <RefreshCw
                            size={18}
                            className={
                                refreshing
                                    ? "spin"
                                    : ""
                            }
                        />

                    </button>

                </div>

                {/* =================================================
                   FILTER PANEL
                ================================================= */}

                <div
                    className={`jobs-filter-panel ${
                        showFilters
                            ? "show"
                            : ""
                    }`}
                >

                    {/* STATUS */}

                    <div className="jobs-filter-group">

                        <label>
                            Status
                        </label>

                        <select
                            value="APPROVED"
                            disabled
                        >
                            <option value="APPROVED">
                                Approved Jobs
                            </option>
                        </select>

                    </div>

                    {/* EMPLOYMENT TYPE */}

                    <div className="jobs-filter-group">

                        <label>
                            Employment Type
                        </label>

                        <select
                            value={
                                employeeTypeFilter
                            }
                            onChange={(e) =>
                                setEmployeeTypeFilter(
                                    e.target.value
                                )
                            }
                        >

                            {employeeTypes.map(
                                (type) => (
                                    <option
                                        key={type}
                                        value={type}
                                    >
                                        {type ===
                                        "ALL"
                                            ? "All Types"
                                            : type}
                                    </option>
                                )
                            )}

                        </select>

                    </div>

                    {/* EXPERIENCE */}

                    <div className="jobs-filter-group">

                        <label>
                            Experience
                        </label>

                        <select
                            value={
                                experienceFilter
                            }
                            onChange={(e) =>
                                setExperienceFilter(
                                    e.target.value
                                )
                            }
                        >

                            <option value="ALL">
                                All Experience
                            </option>

                            <option value="FRESHER">
                                Fresher
                            </option>

                            <option value="0-2">
                                0 – 2 Years
                            </option>

                            <option value="3-5">
                                3 – 5 Years
                            </option>

                            <option value="5+">
                                5+ Years
                            </option>

                        </select>

                    </div>

                    {/* SORT */}

                    <div className="jobs-filter-group">

                        <label>
                            Sort By
                        </label>

                        <select
                            value={sortBy}
                            onChange={(e) =>
                                setSortBy(
                                    e.target.value
                                )
                            }
                        >

                            <option value="newest">
                                Newest First
                            </option>

                            <option value="oldest">
                                Oldest First
                            </option>

                            <option value="salary-high">
                                Highest Salary
                            </option>

                            <option value="salary-low">
                                Lowest Salary
                            </option>

                        </select>

                    </div>

                    {activeFilterCount >
                        0 && (
                        <button
                            type="button"
                            className="clear-filters-btn"
                            onClick={
                                clearFilters
                            }
                        >
                            <X size={15} />
                            Clear
                        </button>
                    )}

                </div>

            </section>

            {/* =================================================
               CONTENT
            ================================================= */}

            <main className="jobs-content">

                <div className="jobs-section-heading">

                    <div>

                        <span className="section-mini-label">
                            EXPLORE ROLES
                        </span>

                        <h2>
                            Latest{" "}
                            <span>
                                Job Openings
                            </span>
                        </h2>

                    </div>

                    <div className="jobs-result-count">
                        {filteredJobs.length}{" "}
                        {filteredJobs.length ===
                        1
                            ? "position"
                            : "positions"}{" "}
                        found
                    </div>

                </div>

                {/* =================================================
                   LOADING
                ================================================= */}

                {loading ? (

                    <div className="jobs-loading-grid">

                        {Array.from({
                            length: 6,
                        }).map(
                            (_, index) => (
                                <div
                                    className="job-skeleton-card"
                                    key={
                                        index
                                    }
                                >

                                    <div className="skeleton-line skeleton-logo"></div>

                                    <div className="skeleton-line skeleton-title"></div>

                                    <div className="skeleton-line skeleton-small"></div>

                                    <div className="skeleton-line skeleton-medium"></div>

                                    <div className="skeleton-line skeleton-tags"></div>

                                    <div className="skeleton-bottom"></div>

                                </div>
                            )
                        )}

                    </div>

                ) : error ? (

                    /* =================================================
                       ERROR
                    ================================================= */

                    <div className="jobs-empty-state">

                        <div className="empty-icon">
                            <BriefcaseBusiness
                                size={30}
                            />
                        </div>

                        <h3>
                            Unable to load jobs
                        </h3>

                        <p>
                            {error}
                        </p>

                        <button
                            type="button"
                            onClick={() =>
                                loadJobs()
                            }
                            className="empty-action-btn"
                        >
                            Try Again
                        </button>

                    </div>

                ) : paginatedJobs.length ===
                  0 ? (

                    /* =================================================
                       EMPTY
                    ================================================= */

                    <div className="jobs-empty-state">

                        <div className="empty-icon">
                            <Search size={30} />
                        </div>

                        <h3>
                            No approved jobs found
                        </h3>

                        <p>
                            There are currently no
                            approved job openings
                            matching your search
                            or filters.
                        </p>

                        <button
                            type="button"
                            onClick={
                                clearFilters
                            }
                            className="empty-action-btn"
                        >
                            Clear Filters
                        </button>

                    </div>

                ) : (

                    /* =================================================
                       JOB GRID
                    ================================================= */

                    <>

                        <div className="jobs-grid">

                            {paginatedJobs.map(
                                (job) => (
                                    <JobCard
                                        key={
                                            job.jobId ||
                                            job.id
                                        }
                                        job={job}
                                        onViewDetails={() =>
                                            setSelectedJob(
                                                job
                                            )
                                        }
                                    />
                                )
                            )}

                        </div>

                        {/* =================================================
                           PAGINATION
                        ================================================= */}

                        {totalPages > 1 && (
                            <div className="jobs-pagination">

                                <button
                                    type="button"
                                    disabled={
                                        currentPage ===
                                        1
                                    }
                                    onClick={() =>
                                        setCurrentPage(
                                            (page) =>
                                                Math.max(
                                                    1,
                                                    page -
                                                        1
                                                )
                                        )
                                    }
                                >

                                    <ChevronLeft
                                        size={18}
                                    />

                                    <span>
                                        Previous
                                    </span>

                                </button>

                                <div className="page-numbers">

                                    {Array.from(
                                        {
                                            length:
                                                totalPages,
                                        },
                                        (
                                            _,
                                            index
                                        ) =>
                                            index +
                                            1
                                    ).map(
                                        (
                                            page
                                        ) => (
                                            <button
                                                type="button"
                                                key={
                                                    page
                                                }
                                                className={
                                                    currentPage ===
                                                    page
                                                        ? "active"
                                                        : ""
                                                }
                                                onClick={() =>
                                                    setCurrentPage(
                                                        page
                                                    )
                                                }
                                            >
                                                {
                                                    page
                                                }
                                            </button>
                                        )
                                    )}

                                </div>

                                <button
                                    type="button"
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                    onClick={() =>
                                        setCurrentPage(
                                            (page) =>
                                                Math.min(
                                                    totalPages,
                                                    page +
                                                        1
                                                )
                                        )
                                    }
                                >

                                    <span>
                                        Next
                                    </span>

                                    <ChevronRight
                                        size={18}
                                    />

                                </button>

                            </div>
                        )}

                    </>

                )}

            </main>

            {/* =================================================
               BOTTOM CTA
            ================================================= */}

            <section className="jobs-bottom-cta">

                <div>

                    <span>
                        LOOKING FOR TALENT?
                    </span>

                    <h2>
                        Are you a company looking
                        <br />
                        for skilled trainers?
                    </h2>

                </div>

                <button
                    type="button"
                    onClick={() =>
                        navigate(
                            "/partner-register"
                        )
                    }
                >

                    Become a Partner

                    <ChevronRight
                        size={18}
                    />

                </button>

            </section>

            {/* =================================================
               JOB DETAILS MODAL
            ================================================= */}

            {selectedJob && (
                <JobDetailsModal
                    job={selectedJob}
                    onClose={() =>
                        setSelectedJob(null)
                    }
                />
            )}

        </div>
    );
};

export default Jobs;